import { useEffect } from "react";
import { DialogContent, DialogTitle, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Check, TriangleAlert } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { WaterEntry, PollutantDocWithId } from "@/types/water";
import { useWaterForm } from "../../hooks/useWaterForm";
import { WaterSourcesTab, WaterDischargeTab, WaterSludgeTab } from "./water";

interface WaterModalFormProps {
  onClose: () => void;
  onSave: () => void;
  formEntry: WaterEntry;
  onFormEntryChange: (entry: WaterEntry) => void;
  waterSources: string[];
  dischargeTypes: string[];
  pollutants: PollutantDocWithId[];
  editing: boolean;
}

export const WaterModalForm = ({
  onClose,
  onSave,
  formEntry,
  onFormEntryChange,
  waterSources,
  dischargeTypes,
  pollutants,
  editing,
}: WaterModalFormProps) => {
  const { t } = useLanguage();
  const {
    errors,
    activeTab,
    touched,
    setActiveTab,
    getTabStatus,
    isFormValid,
    handleSaveClick,
    handleFieldChange,
    handleFieldBlur,
    resetForm,
  } = useWaterForm({
    formEntry,
    editing,
    onSave,
    t,
  });

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const tabs = [
    { id: "sources", label: t("water.sources") },
    ...(formEntry.discharge.hasDischarge === true ? [{ id: "discharge", label: t("water.discharge") }] : []),
    ...(formEntry.sludge.hasSludge === true ? [{ id: "sludge", label: t("water.sludge") }] : []),
  ];

  useEffect(() => {
    const tabIds = tabs.map(tab => tab.id);
    if (!tabIds.includes(activeTab)) {
      setActiveTab("sources");
    }
  }, [tabs, activeTab, setActiveTab]);

  return (
    <DialogContent className="sm:max-w-[800px] max-h-[90vh] flex flex-col">
      <DialogHeader className="flex-shrink-0">
        <DialogTitle>{editing ? t("water.editWaterEntry") : t("water.addWaterEntry")}</DialogTitle>
      </DialogHeader>

      <div className="w-full flex flex-col flex-1 min-h-0">
        <div className="border-b border-gray-200 mb-6">
          <TooltipProvider>
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const isCompleted = getTabStatus(tab.id);
                const isActive = activeTab === tab.id;

                const getTooltipMessage = (tabId: string) => {
                  switch (tabId) {
                    case "sources":
                      return t("water.sourcesTabTooltip");
                    case "discharge":
                      return t("water.dischargeTabTooltip");
                    case "sludge":
                      return t("water.sludgeTabTooltip");
                    default:
                      return "Please complete this section";
                  }
                };

                const tabButton = (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    tabIndex={-1}
                    className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                      isActive
                        ? isCompleted
                          ? "border-teal-500 text-teal-600"
                          : "border-orange-500 text-orange-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    <div
                      className={`flex items-center gap-2 ${
                        isActive ? "" : "group-hover:scale-105 transition-transform"
                      }`}
                    >
                      {isCompleted ? (
                        <div className="flex items-center justify-center w-5 h-5 rounded-full bg-teal-600">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center w-5 h-5 rounded-full bg-orange-100">
                          <TriangleAlert className="h-3 w-3 text-orange-600" />
                        </div>
                      )}
                      <span>{tab.label}</span>
                    </div>
                  </button>
                );

                return isCompleted ? (
                  tabButton
                ) : (
                  <Tooltip key={tab.id}>
                    <TooltipTrigger asChild>{tabButton}</TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs">
                      <p>{getTooltipMessage(tab.id)}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </nav>
          </TooltipProvider>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {activeTab === "sources" && (
            <div className="space-y-4">
              <WaterSourcesTab
                formEntry={formEntry}
                onFormEntryChange={onFormEntryChange}
                waterSources={waterSources}
                errors={errors}
                touched={touched}
                onFieldChange={handleFieldChange}
                onFieldBlur={handleFieldBlur}
              />
            </div>
          )}

          {activeTab === "discharge" && formEntry.discharge.hasDischarge === true && (
            <div className="space-y-4">
              <WaterDischargeTab
                formEntry={formEntry}
                onFormEntryChange={onFormEntryChange}
                dischargeTypes={dischargeTypes}
                pollutants={pollutants}
                errors={errors}
                touched={touched}
                onFieldChange={handleFieldChange}
                onFieldBlur={handleFieldBlur}
              />
            </div>
          )}

          {activeTab === "sludge" && formEntry.sludge.hasSludge === true && (
            <div className="space-y-4">
              <WaterSludgeTab
                formEntry={formEntry}
                onFormEntryChange={onFormEntryChange}
                pollutants={pollutants}
                errors={errors}
                touched={touched}
                onFieldChange={handleFieldChange}
                onFieldBlur={handleFieldBlur}
              />
            </div>
          )}
        </div>
      </div>

      <DialogFooter className="flex-shrink-0">
        <Button variant="outline" onClick={handleClose}>
          {t("common.cancel")}
        </Button>
        <Button variant="teal" onClick={handleSaveClick} disabled={!isFormValid()}>
          {editing ? t("common.save") : t("common.add")}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};
